import React, { useState, useEffect, useRef } from 'react';
import { updateGame, db, doc, runTransaction, increment } from '../services/firebase';
import toast from 'react-hot-toast';

const QuickScorePhase = ({ game, userId, onComplete }) => {
  const [scored, setScored] = useState(null); // true/false
  const [canUndo, setCanUndo] = useState(false);
  const [undoTimeLeft, setUndoTimeLeft] = useState(3);
  const [scoredBy, setScoredBy] = useState(null);
  const undoIntervalRef = useRef(null);

  const myTeam = game?.players?.[userId]?.team;
  const wordSelectingTeam = game?.currentRoundData?.wordSelectingTeam;
  const performingTeam = game?.currentRoundData?.performingTeam;
  const guessingTeam = performingTeam; // Performing team = guessing team!
  const canScore = myTeam === wordSelectingTeam;
  const selectedWord = game?.currentRoundData?.selectedWord;
  const wasGuessedCorrectly = game?.currentRoundData?.correctlyGuessed;
  const guessedByName = game?.currentRoundData?.guessedByName;
  const guessedByTeam = game?.currentRoundData?.guessedByTeam;

  // Check if Firebase data is fully loaded (prevent actions during screen wake/sync)
  const isDataLoaded = selectedWord && wordSelectingTeam && performingTeam;

  // Check if round is already complete (prevent duplicate scoring on screen wake)
  const isRoundComplete = game?.gamePhase === 'roundComplete';

  // VERSION TAG for debugging deployment
  console.log('🏷️ QuickScorePhase VERSION: 2024-12-02-v13-final-fix');

  // Debug log on mount
  useEffect(() => {
    console.log('📊 QuickScorePhase loaded:');
    console.log(`   Word-selecting team: ${wordSelectingTeam}`);
    console.log(`   Guessing team: ${guessingTeam}`);
    console.log(`   Was guessed correctly: ${wasGuessedCorrectly}`);
    console.log(`   Guessed by: ${guessedByName} (${guessedByTeam})`);
  }, []);

  // Auto-score if word was typed correctly (skip confirmation)
  useEffect(() => {
    console.log('🔍 AUTO-SCORE CHECK:', {
      wasGuessedCorrectly,
      scored,
      firebaseScored: game?.currentRoundData?.scored,
      firebaseScoredBy: game?.currentRoundData?.scoredBy,
      firebaseFinalized: game?.currentRoundData?.finalized,
      isDataLoaded,
      isRoundComplete,
      shouldAutoScore: isDataLoaded && !isRoundComplete && wasGuessedCorrectly && scored === null && !game?.currentRoundData?.scored && !game?.currentRoundData?.scoredBy && !game?.currentRoundData?.finalized
    });

    // Don't auto-score if data isn't fully loaded (screen wake issue)
    if (!isDataLoaded) {
      console.log('⏳ Waiting for Firebase data to load...');
      return;
    }

    // Don't auto-score if round is already complete (prevents duplicate scoring on screen wake)
    if (isRoundComplete) {
      console.log('🛑 Round already complete - skipping auto-score to prevent duplicate');
      return;
    }

    // Don't auto-score if round has been finalized (prevents duplicate scoring)
    if (game?.currentRoundData?.finalized === true) {
      console.log('🛑 Round already finalized - skipping auto-score to prevent duplicate');
      return;
    }

    // Don't auto-score if scoring already happened (check scoredBy field)
    if (game?.currentRoundData?.scoredBy) {
      console.log('🛑 Scoring already happened (scoredBy exists) - skipping auto-score to prevent duplicate');
      return;
    }

    // Check if scored is falsy (null or undefined) AND scoredBy doesn't exist
    if (wasGuessedCorrectly && scored === null && !game?.currentRoundData?.scored) {
      console.log('✅ AUTO-SCORE TRIGGERED!');

      // Word was typed correctly - auto-award point to guessing team
      const autoScore = async () => {
        try {
          console.log(`🎯 Setting scored=true in Firebase for guessing team: ${guessingTeam}`);

          // CRITICAL: Set scoredBy to current userId. This is the key to the fix.
          // The manual score listener will see this and the device with this userId will finalize.
          await updateGame(game.gameId, {
            'currentRoundData.scored': true,
            'currentRoundData.scoredBy': userId, // Use userId instead of 'auto'!
            'currentRoundData.scoredByName': guessedByName
          });

          console.log('✅ Firebase updated with scored=true');
          console.log(`   I (${userId}) have INITIATED the auto-score. The listener will now finalize.`);
        } catch (error) {
          console.error('❌ Error auto-scoring:', error);
          console.log('ℹ️ Another device probably updated Firebase first - I will not finalize');
        }
      };

      autoScore();
    }
  }, [wasGuessedCorrectly, game?.currentRoundData?.scored, game?.currentRoundData?.scoredBy, game?.currentRoundData?.finalized, scored, isDataLoaded, isRoundComplete, userId, guessedByName, guessingTeam]);

  // Listen for score from Firebase (when manually scored OR auto-scored)
  // This is now the SINGLE SOURCE OF TRUTH for finalizing a score.
  useEffect(() => {
    console.log('🔍 SCORE LISTENER CHECK (Handles Manual & Auto):', {
      firebaseScored: game?.currentRoundData?.scored,
      scoredBy: game?.currentRoundData?.scoredBy,
      firebaseFinalized: game?.currentRoundData?.finalized,
      localScored: scored,
      isDataLoaded,
      isRoundComplete,
      amIScorer: game?.currentRoundData?.scoredBy === userId,
      shouldRun: isDataLoaded &&
                 !isRoundComplete &&
                 !game?.currentRoundData?.finalized &&
                 (game?.currentRoundData?.scored === true || game?.currentRoundData?.scored === false) &&
                 game?.currentRoundData?.scoredBy && // Ensure scoredBy is present
                 scored === null
    });

    // Don't process if data isn't fully loaded (screen wake issue)
    if (!isDataLoaded) {
      console.log('⏳ Waiting for Firebase data to load...');
      return;
    }

    // Don't process if round is already complete (prevents duplicate scoring on screen wake)
    if (isRoundComplete) {
      console.log('🛑 Round already complete - skipping listener to prevent duplicate');
      return;
    }

    // Don't process if round has been finalized (prevents duplicate scoring)
    if (game?.currentRoundData?.finalized === true) {
      console.log('🛑 Round already finalized - skipping listener to prevent duplicate');
      return;
    }

    // Only trigger if scored is explicitly true or false (not null/undefined)
    // and this client hasn't processed the score yet (scored === null)
    if ((game?.currentRoundData?.scored === true || game?.currentRoundData?.scored === false) &&
        game?.currentRoundData?.scoredBy &&
        scored === null) {
      const scoreValue = game.currentRoundData.scored;
      const scoredByUserId = game.currentRoundData.scoredBy;
      const isMyScore = scoredByUserId === userId;

      console.log(`⚠️ SCORE LISTENER TRIGGERED! scoreValue=${scoreValue}, scoredBy=${scoredByUserId}, isMyScore=${isMyScore}`);
      console.log(`   wasGuessedCorrectly: ${wasGuessedCorrectly}`);

      setScored(scoreValue);
      setScoredBy(scoredByUserId);

      // CRITICAL FIX: Only the device that originally scored should finalize
      // Other devices should just display the result and wait for phase change
      if (isMyScore) {
        // This is an auto-score (word was typed correctly), finalize immediately.
        if (wasGuessedCorrectly) {
            console.log('✅ This is an AUTO-SCORE. I am the scorer, so I will finalize immediately.');
            setCanUndo(false);
            // Using a small timeout to allow the toast to be seen before the phase changes
            setTimeout(() => {
                finalizeScoreWithValue(scoreValue);
            }, 1000);
        } else {
            // This is a MANUAL score, start the undo countdown.
            console.log('✅ This is a MANUAL score. I am the scorer, starting undo countdown and will finalize.');
            setCanUndo(true); // Only show undo UI on scoring device

            // Start undo countdown - only on the device that scored
            undoIntervalRef.current = setInterval(() => {
              setUndoTimeLeft(prev => {
                if (prev <= 1) {
                  clearInterval(undoIntervalRef.current);
                  console.log(`🏁 CALLING finalizeScoreWithValue(${scoreValue}) from manual listener (I scored)`);
                  finalizeScoreWithValue(scoreValue);
                  return 0;
                }
                return prev - 1;
              });
            }, 1000);
        }
      } else {
        console.log('ℹ️ Someone else scored - displaying result but NOT finalizing (prevents duplicate points)');
        setCanUndo(false); // Don't show undo UI on other devices
        // Just show the score result, don't start countdown or finalize
        // The scoring device will handle finalization
      }
    }
  }, [game?.currentRoundData?.scored, game?.currentRoundData?.scoredBy, game?.currentRoundData?.finalized, scored, userId, isDataLoaded, isRoundComplete, wasGuessedCorrectly]);

  const handleQuickScore = async (didScore) => {
    if (scored !== null) return;

    // Prevent scoring if round is already complete
    if (isRoundComplete) {
      console.log('🛑 Cannot score - round already complete');
      toast.error('Round already completed');
      return;
    }

    try {
      await updateGame(game.gameId, {
        'currentRoundData.scored': didScore,
        'currentRoundData.scoredBy': userId
      });

      // Local state updates removed from here. The useEffect listener will now handle it.
      toast.success(didScore ? 'Point awarded! ✓' : 'No point');
    } catch (error) {
      console.error('Error scoring:', error);
      toast.error('Failed to score');
    }
  };

  const handleUndo = async () => {
    if (!canUndo) return;

    try {
      if (undoIntervalRef.current) {
        clearInterval(undoIntervalRef.current);
      }

      await updateGame(game.gameId, {
        'currentRoundData.scored': null,
        'currentRoundData.scoredBy': null
      });

      setScored(null);
      setScoredBy(null);
      setCanUndo(false);
      setUndoTimeLeft(3);
      toast.success('Score undone');
    } catch (error) {
      console.error('Error undoing score:', error);
      toast.error('Failed to undo');
    }
  };

  const finalizeScoreWithValue = async (scoreValue) => {
    setCanUndo(false);

    try {
      await runTransaction(db, async (transaction) => {
        const gameRef = doc(db, 'games', game.gameId);
        const gameDoc = await transaction.get(gameRef);

        if (!gameDoc.exists()) {
          throw "Game document does not exist!";
        }

        const gameData = gameDoc.data();
        const currentRound = gameData.currentRound;

        console.log('═══════════════════════════════════════════════');
        console.log('🏁 finalizeScoreWithValue (TRANSACTION) CALLED');
        console.log(`   scoreValue: ${scoreValue}`);
        console.log(`   currentRound: ${currentRound}`);
        console.log(`   gamePhase: ${gameData.gamePhase}`);
        console.log(`   finalized: ${gameData.currentRoundData?.finalized}`);
        console.log(`   scoredRounds[${currentRound}]: ${gameData.scoredRounds?.[currentRound]}`);
        console.log('═══════════════════════════════════════════════');

        // SAFETY CHECKS (inside transaction)
        if (gameData.gamePhase === 'roundComplete' ||
            gameData.currentRoundData?.finalized === true ||
            gameData.scoredRounds?.[currentRound] === true) {
          console.log('🛑 ABORT TRANSACTION: Round already scored.');
          return;
        }

        const updates = {
          gamePhase: 'roundComplete',
          'currentRoundData.finalized': true,
          [`scoredRounds.${currentRound}`]: true,
          'updatedAt': new Date()
        };

        const guessingTeamForScore = gameData.currentRoundData.performingTeam;
        const wordSelectingTeamForScore = gameData.currentRoundData.wordSelectingTeam;

        if (scoreValue) {
          updates[`scores.${guessingTeamForScore}`] = increment(1);
          if (gameData.currentRoundData.guessedBy) {
            updates[`playerPoints.${gameData.currentRoundData.guessedBy}`] = increment(1);
          }
        } else {
          updates[`scores.${wordSelectingTeamForScore}`] = increment(1);
        }

        transaction.update(gameRef, updates);
        console.log(`✅ TRANSACTION SUCCESS: Round ${currentRound} finalized and scored.`);
      });
    } catch (error) {
      console.error("Transaction failed: ", error);
    }
  };

  const finalizeScore = async () => {
    await finalizeScoreWithValue(scored);
  };

  const getPlayerName = (uid) => {
    return game?.players?.[uid]?.name || 'Someone';
  };

  // Show loading state if Firebase data isn't fully loaded (screen wake/sync issue)
  if (!isDataLoaded) {
    return (
      <div style={{
        background: 'white',
        borderRadius: 'var(--radius-xl)',
        padding: '2rem',
        boxShadow: 'var(--shadow-md)',
        textAlign: 'center'
      }}>
        <div className="spinner" style={{ margin: '0 auto 1rem' }}></div>
        <h3 style={{
          fontSize: '1.25rem',
          fontWeight: 700,
          marginBottom: '0.5rem'
        }}>
          Syncing Game State...
        </h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
          Reconnecting to game...
        </p>
      </div>
    );
  }

  // Quick score buttons (only for scoring team)
  // Don't show buttons if word was guessed correctly - auto-score will handle it
  // Don't show buttons if round is already complete
  if (canScore && scored === null && !wasGuessedCorrectly && !isRoundComplete) {
    return (
      <div style={{
        background: 'white',
        borderRadius: 'var(--radius-xl)',
        padding: '2rem',
        boxShadow: 'var(--shadow-md)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏱️</div>
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            marginBottom: '0.5rem'
          }}>
            Time's Up!
          </h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
            Did Team {guessingTeam === 'teamA' ? 'A' : 'B'} guess the word?
          </p>
          <div style={{
            padding: '1rem',
            background: 'var(--bg-tertiary)',
            borderRadius: 'var(--radius-lg)',
            fontSize: '1.25rem',
            fontWeight: 700,
            color: 'var(--primary)',
            marginBottom: '1rem'
          }}>
            {selectedWord}
          </div>

          {/* Show if word was auto-detected as guessed */}
          {wasGuessedCorrectly && (
            <div style={{
              padding: '0.75rem',
              background: '#d1fae5',
              color: '#065f46',
              borderRadius: 'var(--radius)',
              fontSize: '0.875rem',
              fontWeight: 600,
              marginBottom: '1rem'
            }}>
              ✓ {guessedByName} typed it correctly!
            </div>
          )}
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '1rem'
        }}>
          <button
            onClick={() => handleQuickScore(false)}
            className="btn btn-lg"
            style={{
              background: 'var(--error)',
              color: 'white',
              padding: '1.5rem',
              fontSize: '1.125rem'
            }}
          >
            ❌ No
          </button>
          <button
            onClick={() => handleQuickScore(true)}
            className="btn btn-lg"
            style={{
              background: 'var(--success)',
              color: 'white',
              padding: '1.5rem',
              fontSize: '1.125rem'
            }}
          >
            ✓ Yes
          </button>
        </div>

        <p style={{
          marginTop: '1rem',
          fontSize: '0.875rem',
          color: 'var(--text-secondary)',
          textAlign: 'center'
        }}>
          YES = Team {guessingTeam === 'teamA' ? 'A' : 'B'} scores | NO = Team {wordSelectingTeam === 'teamA' ? 'A' : 'B'} scores
        </p>
      </div>
    );
  }

  // Score submitted - undo window
  if (scored !== null && canUndo) {
    return (
      <div style={{
        background: 'white',
        borderRadius: 'var(--radius-xl)',
        padding: '2rem',
        boxShadow: 'var(--shadow-md)',
        textAlign: 'center'
      }}>
        <div style={{
          padding: '2rem',
          background: scored ? '#d1fae5' : '#fee2e2',
          borderRadius: 'var(--radius-lg)',
          marginBottom: '1.5rem'
        }}>
          <div style={{ fontSize: '4rem', marginBottom: '0.5rem' }}>
            {scored ? '✓' : '❌'}
          </div>
          <div style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: scored ? '#065f46' : '#991b1b',
            marginBottom: '0.5rem'
          }}>
            {scored ? 'Point Awarded!' : 'No Point'}
          </div>
          <div style={{
            fontSize: '0.875rem',
            color: scored ? '#065f46' : '#991b1b',
            opacity: 0.8
          }}>
            Scored by {scoredBy === userId ? 'you' : getPlayerName(scoredBy)}
          </div>
        </div>

        {canScore && (
          <>
            <div style={{
              fontSize: '2rem',
              fontWeight: 700,
              color: 'var(--warning)',
              marginBottom: '1rem'
            }}>
              {undoTimeLeft}
            </div>
            <button
              onClick={handleUndo}
              className="btn btn-outline btn-lg"
              style={{
                borderColor: 'var(--warning)',
                color: 'var(--warning)'
              }}
            >
              ↩️ Undo
            </button>
            <p style={{
              marginTop: '0.5rem',
              fontSize: '0.875rem',
              color: 'var(--text-secondary)'
            }}>
              Tap undo within {undoTimeLeft}s to change
            </p>
          </>
        )}

        {!canScore && (
          <div style={{
            padding: '1rem',
            background: 'var(--bg-tertiary)',
            borderRadius: 'var(--radius)',
            color: 'var(--text-secondary)'
          }}>
            Finalizing in {undoTimeLeft}s...
          </div>
        )}
      </div>
    );
  }

  // Waiting for score (phase will change to 'roundComplete' once finalized)
  return (
    <div style={{
      background: 'white',
      borderRadius: 'var(--radius-xl)',
      padding: '2rem',
      boxShadow: 'var(--shadow-md)',
      textAlign: 'center'
    }}>
      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
      <h3 style={{
        fontSize: '1.25rem',
        fontWeight: 700,
        marginBottom: '0.5rem'
      }}>
        Waiting for Score...
      </h3>
      <p style={{ color: 'var(--text-secondary)' }}>
        Team {wordSelectingTeam === 'teamA' ? 'A' : 'B'} is scoring the round
      </p>
    </div>
  );
};

export default QuickScorePhase;
